import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import {
  getAdminReportsSummary,
  getAdminRecommendationReports,
  downloadAdminRecommendationPdf,
} from '../../services/api';

export default function AdminReports() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recReports, setRecReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getAdminReportsSummary(), getAdminRecommendationReports()])
      .then(([sumRes, recRes]) => {
        setSummary(sumRes.data);
        setRecReports(recRes.data.reports || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDownloadRec = async (id) => {
    try {
      const res = await downloadAdminRecommendationPdf(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recommendations-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      alert('Failed to download PDF');
    }
  };

  if (loading) {
    return <p className="text-gray-500 py-12 text-center">Loading reports...</p>;
  }

  const a = summary?.audits || {};
  const passRate = a.completed > 0 ? Math.round((parseInt(a.passed, 10) / parseInt(a.completed, 10)) * 100) : 0;
  const trendData = (summary?.dailyTrend || []).map((d) => ({
    day: new Date(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    passed: parseInt(d.passed, 10) || 0,
    failed: parseInt(d.failed_score, 10) || 0,
    total: parseInt(d.total, 10) || 0,
  }));

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">Reports Hub</h2>
      <p className="text-gray-500 mb-6">
        Audit outcomes across the platform (pass = score ≥ {summary?.passThreshold || 80})
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Audits" value={a.total || 0} />
        <StatCard label="Completed" value={a.completed || 0} color="text-teal-600" />
        <StatCard label="Passed" value={a.passed || 0} color="text-green-600" sub={`${passRate}% pass rate`} />
        <StatCard label="Failed / Low Score" value={a.failed_score || 0} color="text-red-600" />
        <StatCard label="Processing" value={a.processing || 0} color="text-yellow-600" />
        <StatCard label="Avg Score" value={Math.round(parseFloat(a.avg_score) || 0)} color="text-teal-600" />
        <StatCard label="Critical Findings" value={a.total_critical || 0} color="text-red-600" />
        <StatCard label="Rec. Reports" value={summary?.recommendationReports?.total || 0} />
      </div>

      {trendData.length > 0 && (
        <div className="glass-card rounded-2xl border border-teal-100/50 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily Audit Trend (30 days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="passed" stroke="#16a34a" name="Passed" strokeWidth={2} />
              <Line type="monotone" dataKey="failed" stroke="#dc2626" name="Failed score" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="glass-card rounded-2xl border border-teal-100/50 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">By Agency</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-teal-50/50 text-left text-gray-500">
              <th className="px-4 py-3">Agency</th>
              <th className="px-4 py-3 text-center">Audits</th>
              <th className="px-4 py-3 text-center">Passed</th>
              <th className="px-4 py-3 text-center">Failed</th>
              <th className="px-4 py-3 text-center">Avg Score</th>
              <th className="px-4 py-3">Last Audit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {(summary?.byAgency || []).length === 0 ? (
              <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-400">No audit data yet</td></tr>
            ) : summary.byAgency.map((row) => (
              <tr key={row.id} className="hover:bg-teal-50/30">
                <td className="px-4 py-3 font-medium text-gray-800">{row.name}</td>
                <td className="px-4 py-3 text-center">{row.audit_count}</td>
                <td className="px-4 py-3 text-center text-green-600 font-semibold">{row.passed}</td>
                <td className="px-4 py-3 text-center text-red-600 font-semibold">{row.failed_score}</td>
                <td className="px-4 py-3 text-center">{Math.round(parseFloat(row.avg_score) || 0)}</td>
                <td className="px-4 py-3 text-gray-500">
                  {row.last_audit_at ? new Date(row.last_audit_at).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass-card rounded-2xl border border-teal-100/50 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Recommendation Reports</h3>
          <p className="text-sm text-gray-500">Auto-generated weakness summaries per agency</p>
        </div>
        {recReports.length === 0 ? (
          <p className="px-6 py-8 text-center text-gray-400">No recommendation reports yet</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {recReports.map((r) => (
              <div key={r.id} className="px-6 py-4 flex items-center justify-between hover:bg-teal-50/30">
                <div>
                  <p className="font-medium text-gray-800">{r.agency_name}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(r.generated_at).toLocaleDateString()} · {r.audit_count} audits · {r.total_weaknesses || 0} issues
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownloadRec(r.id)}
                  className="text-sm bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg"
                >
                  PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-sm text-gray-400 mt-4">
        View individual audits in{' '}
        <button type="button" onClick={() => navigate('/admin/audits')} className="text-teal-600 hover:underline">
          All Audits
        </button>
      </p>
    </div>
  );
}

function StatCard({ label, value, color = 'text-gray-800', sub }) {
  return (
    <div className="glass-card rounded-2xl border border-teal-100/50 p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}
